import { useRef } from "react";
import { appConfig } from "../../app-config";
import { tokenStore } from "core/authentication";

const useRealtimeSocketProvider = ({
    onRecognizing,
    onRecognized,
    onSynthesized,
    onStartListening,
    onStopListening,
    onStartSynthesizing,
    onStopSynthesizing,
    onError,
}) => {
    // Core refs
    const wsRef = useRef(null);
    const sessionRef = useRef(null);

    const audioContextRef = useRef(null);
    const micStreamRef = useRef(null);
    const workletNodeRef = useRef(null);

    const isListeningRef = useRef(false);
    const isSynthesizingRef = useRef(false);
    const idleTimerRef = useRef(null);
    const SOCKET_IDLE_TIMEOUT_MS = 120 * 1000; // 1 minute

    // Language config
    const sourceLangRef = useRef(null);
    const targetLangRef = useRef(null);


    // Buffers
    const inputTranscriptBufferRef = useRef("");
    const translatedTextBufferRef = useRef("");
    const ttsChunksRef = useRef([]);

    // Azure-shaped NO-OPs (kept for compatibility)
    const initConfigs = async () => { };
    const initSynthesizer = async () => { };
    const updateAuthToken = async () => { };
    const synthesizeText = async () => { }; // Realtime already handles TTS

    const disposeAzureSpeech = async () => {
        try {
            await stopListening();
        } catch { }
    };

    const resetIdleTimer = () => {
        if (idleTimerRef.current) {
            clearTimeout(idleTimerRef.current);
        }

        idleTimerRef.current = setTimeout(async () => {
            console.warn("Socket idle for 60s. Closing realtime session.");
            await stopListening();
            if (wsRef.current) {
                wsRef.current.close();
                wsRef.current = null;
            }
        }, SOCKET_IDLE_TIMEOUT_MS);
    };

    // Init language config
    const initAzureSpeech = async ([sourceLang, targetLang]) => {
        sourceLangRef.current = sourceLang;
        targetLangRef.current = targetLang;
    };

    const emitRecognizingPreview = () => {
        onRecognizing?.({
            source: {
                language: sourceLangRef.current,
                text: inputTranscriptBufferRef.current,
            },
            target: {
                language: targetLangRef.current,
                text: translatedTextBufferRef.current,
            }
        });
    };
    // Start listening
    const startListening = async () => {
        if (isListeningRef.current) return;
        isListeningRef.current = true;
        sessionRef.current = Date.now().toString();

        inputTranscriptBufferRef.current = "";
        translatedTextBufferRef.current = "";
        ttsChunksRef.current = [];
        isSynthesizingRef.current = false;

        const bearerToken = await tokenStore.getAuthToken();
        const socketurl = `${appConfig.API_URL}/assessment/translate-audio?token=${encodeURIComponent(bearerToken)}`.replace("http", "ws")
        wsRef.current = new WebSocket(
            socketurl
        );

        wsRef.current.onerror = (err) => onError?.(err);

        wsRef.current.onopen = () => {
            wsRef.current.send(
                JSON.stringify({
                    type: "config",
                    source_language: sourceLangRef.current,
                    target_language: targetLangRef.current,
                })
            );
        };
        setTimeout(() => onStartListening?.(), 500);

        wsRef.current.onmessage = async (event) => {
            //resetIdleTimer();
            const data = JSON.parse(event.data);
            switch (data.type) {
                // Speech lifecycle              
                case "input_audio_buffer.speech_started":
                    inputTranscriptBufferRef.current = "";
                    translatedTextBufferRef.current = "";
                    break;

                case "input_audio_buffer.speech_stopped":
                    onStopListening?.();
                    break;

                // INPUT TRANSCRIPTION (STT)             
                case "conversation.item.input_audio_transcription.delta":
                    if (data.delta) {
                        inputTranscriptBufferRef.current += data.delta;
                        emitRecognizingPreview();
                    }
                    break;

                case "conversation.item.input_audio_transcription.completed":
                    if (data.transcript) {
                        inputTranscriptBufferRef.current = data.transcript;
                        emitRecognizingPreview();
                    }
                    break;

                // TRANSLATED TEXT (from audio transcript)              
                case "response.audio_transcript.delta":
                    if (data.delta) {
                        translatedTextBufferRef.current += data.delta;
                        emitRecognizingPreview();
                    }
                    break;

                case "response.audio_transcript.done": {
                    const transcription = inputTranscriptBufferRef.current.trim();
                    const translation =
                        data.transcript?.trim() || translatedTextBufferRef.current.trim();

                    inputTranscriptBufferRef.current = "";
                    translatedTextBufferRef.current = "";

                    onRecognized?.(
                        {
                            transcription,
                            translation,
                            isRealtime: true,
                            target_language: data.translated_language
                        },
                        sessionRef.current
                    );
                    break;
                }


                // AUDIO STREAM (TTS)             
                case "response.audio.delta":
                    if (!isSynthesizingRef.current) {
                        isSynthesizingRef.current = true;
                        onStartSynthesizing?.();
                        await stopListening();
                    }
                    bufferTtsAudio(data.delta);
                    break;

                case "response.audio.done":
                    await emitSynthesizedAudio();
                    onStopSynthesizing?.();
                    break;

                case "error":
                    onError?.(data);
                    break;
            }
        };

        // Open mic + AudioWorklet   
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        micStreamRef.current = stream;

        const audioContext = new AudioContext({ sampleRate: 16000 });
        audioContextRef.current = audioContext;

        await audioContext.audioWorklet.addModule("/pcm16-processor.js");

        const source = audioContext.createMediaStreamSource(stream);
        const workletNode = new AudioWorkletNode(audioContext, "pcm16-processor");
        workletNodeRef.current = workletNode;

        workletNode.port.onmessage = (event) => {
            if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;

            wsRef.current.send(
                JSON.stringify({
                    type: "audio",
                    data: arrayBufferToBase64(event.data),
                })
            );
        };

        source.connect(workletNode);
    };

    // Stop listening (hard stop)
    const stopListening = async () => {
        if (!isListeningRef.current) return;
        isListeningRef.current = false;
        if (workletNodeRef.current) {
            workletNodeRef.current.port.close();
            workletNodeRef.current.disconnect();
            workletNodeRef.current = null;
        }

        if (micStreamRef.current) {
            micStreamRef.current.getTracks().forEach((t) => t.stop());
            micStreamRef.current = null;
        }

        if (audioContextRef.current) {
            await audioContextRef.current.close();
            audioContextRef.current = null;
        }

        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({ type: "commit_audio" }));
        }
    };

    // Audio helpers
    const bufferTtsAudio = (base64) => {
        const binary = atob(base64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) {
            bytes[i] = binary.charCodeAt(i);
        }
        ttsChunksRef.current.push(bytes);
    };

    const emitSynthesizedAudio = async () => {
        if (!ttsChunksRef.current.length) return;

        const pcm16 = mergePcm16FromUint8Chunks(ttsChunksRef.current);
        const wavBuffer = createWavFile(pcm16, 24000);
        const blob = new Blob([wavBuffer], { type: "audio/wav" });

        await onSynthesized?.(blob, sessionRef.current);
        ttsChunksRef.current = [];
    };

    const mergePcm16FromUint8Chunks = (chunks) => {
        const totalSamples = chunks.reduce((s, c) => s + c.byteLength / 2, 0);
        const pcm16 = new Int16Array(totalSamples);
        let offset = 0;

        for (const chunk of chunks) {
            const samples = new Int16Array(
                chunk.buffer,
                chunk.byteOffset,
                chunk.byteLength / 2
            );
            pcm16.set(samples, offset);
            offset += samples.length;
        }
        return pcm16;
    };

    const createWavFile = (pcm16, sampleRate) => {
        const buffer = new ArrayBuffer(44 + pcm16.length * 2);
        const view = new DataView(buffer);

        writeString(view, 0, "RIFF");
        view.setUint32(4, 36 + pcm16.length * 2, true);
        writeString(view, 8, "WAVE");
        writeString(view, 12, "fmt ");
        view.setUint32(16, 16, true);
        view.setUint16(20, 1, true);
        view.setUint16(22, 1, true);
        view.setUint32(24, sampleRate, true);
        view.setUint32(28, sampleRate * 2, true);
        view.setUint16(32, 2, true);
        view.setUint16(34, 16, true);
        writeString(view, 36, "data");
        view.setUint32(40, pcm16.length * 2, true);

        let offset = 44;
        for (let i = 0; i < pcm16.length; i++, offset += 2) {
            view.setInt16(offset, pcm16[i], true);
        }

        return buffer;
    };

    const writeString = (view, offset, str) => {
        for (let i = 0; i < str.length; i++) {
            view.setUint8(offset + i, str.charCodeAt(i));
        }
    };

    const arrayBufferToBase64 = (buffer) => {
        const bytes = new Uint8Array(buffer);
        let binary = "";
        for (let i = 0; i < bytes.length; i++) {
            binary += String.fromCharCode(bytes[i]);
        }
        return btoa(binary);
    };

    return {
        initConfigs,
        initAzureSpeech,
        initSynthesizer,
        startListening,
        stopListening,
        synthesizeText,
        disposeAzureSpeech,
        updateAuthToken,
        sessionRef,
    };
};

export default useRealtimeSocketProvider;
